import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Paper,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  LinearProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Business,
  Psychology,
  Settings,
  Refresh,
  AutoAwesome,
  Store,
  Warehouse,
  LocalShipping,
  Inventory,
  TrendingUp
} from '@mui/icons-material';

// Business Rule Interface
interface BusinessRule {
  id: string;
  name: string;
  description: string;
  category: 'operational' | 'financial' | 'inventory' | 'shipping' | 'customer' | 'warehouse';
  active: boolean;
  priority: 'low' | 'medium' | 'high' | 'critical';
  conditions: string[];
  actions: string[];
  lastModified: Date;
  createdBy: string;
  version: string;
}

// Company Information Interface
interface CompanyInfo {
  name: string;
  warehouseHours: string;
  orderVolume: string;
  timezone: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  taxId: string;
  businessType: string;
  foundedYear: number;
  employeeCount: number;
}

// Marketplace Configuration Interface
interface MarketplaceConfig {
  id: string;
  cartId: number;
  name: string;
  active: boolean;
  specialHandling: boolean;
  fulfillmentRules: string[];
  leadTime: number;
  packagingRequirements: string[];
  hazmatCompliance: boolean;
  returnPolicy: string;
  lastUpdated: Date;
}

// Business Rules Categories
const BUSINESS_RULE_CATEGORIES = {
  operational: { label: 'Operational', icon: <Settings />, color: 'primary' },
  financial: { label: 'Financial', icon: <TrendingUp />, color: 'success' },
  inventory: { label: 'Inventory', icon: <Inventory />, color: 'warning' },
  shipping: { label: 'Shipping', icon: <LocalShipping />, color: 'info' },
  customer: { label: 'Customer', icon: <Business />, color: 'secondary' },
  warehouse: { label: 'Warehouse', icon: <Warehouse />, color: 'error' }
};

export function AdminBusinessRulesManager() {
  const [businessRules, setBusinessRules] = useState<BusinessRule[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [marketplaceConfigs, setMarketplaceConfigs] = useState<MarketplaceConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Dialog states
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [marketplaceDialogOpen, setMarketplaceDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<BusinessRule | null>(null);
  const [editingMarketplace, setEditingMarketplace] = useState<MarketplaceConfig | null>(null);
  
  // Form states
  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    category: 'operational' as BusinessRule['category'],
    active: true,
    priority: 'medium' as BusinessRule['priority'],
    conditions: [''],
    actions: ['']
  });
  
  const [marketplaceForm, setMarketplaceForm] = useState({
    cartId: 0,
    name: '',
    active: true,
    specialHandling: false,
    fulfillmentRules: [''],
    leadTime: 2,
    packagingRequirements: [''],
    hazmatCompliance: false,
    returnPolicy: ''
  });
  
  // Notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Load mock data for development
  useEffect(() => {
    const loadMockData = async () => {
      setIsLoading(true);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock Business Rules
      setBusinessRules([
        {
          id: '1',
          name: 'Order Approval Workflow',
          description: 'Standard order approval process for all channels',
          category: 'operational',
          active: true,
          priority: 'high',
          conditions: ['Order total > $0', 'Customer email valid', 'Payment confirmed'],
          actions: ['Set status to approved', 'Send confirmation email', 'Add to warehouse queue'],
          lastModified: new Date(Date.now() - 24 * 60 * 60 * 1000),
          createdBy: 'admin@sleekshop.com',
          version: '1.2'
        },
        {
          id: '2',
          name: 'Warehouse Capacity Limits',
          description: 'Prevent overloading warehouse during peak hours',
          category: 'warehouse',
          active: true,
          priority: 'critical',
          conditions: ['Current orders > 1500', 'Time between 6am-2pm PST', 'Monday or Tuesday'],
          actions: ['Delay new order processing', 'Send capacity alert', 'Prioritize existing orders'],
          lastModified: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          createdBy: 'warehouse@sleekshop.com',
          version: '2.1'
        },
        {
          id: '3',
          name: 'Inventory Reorder Points',
          description: 'Automatic reorder triggers based on stock levels',
          category: 'inventory',
          active: true,
          priority: 'high',
          conditions: ['QOH < ReorderPoint', 'Product is active', 'Supplier available'],
          actions: ['Generate purchase order', 'Send reorder notification', 'Update inventory status'],
          lastModified: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
          createdBy: 'inventory@sleekshop.com',
          version: '1.5'
        },
        {
          id: '4',
          name: 'Amazon Hazmat Compliance',
          description: 'Special handling for Amazon hazmat products',
          category: 'shipping',
          active: true,
          priority: 'critical',
          conditions: ['CartID = 2 (Amazon)', 'Product marked as hazmat', 'Order approved'],
          actions: ['Apply hazmat packaging', 'Add compliance labels', 'Extended processing time'],
          lastModified: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
          createdBy: 'compliance@sleekshop.com',
          version: '1.8'
        }
      ]);

      // Mock Company Information
      setCompanyInfo({
        name: 'SleekShop Beauty',
        warehouseHours: '6:00 AM - 2:30 PM PST',
        orderVolume: '2,000+ orders per day',
        timezone: 'Pacific Standard Time (PST)',
        address: '123 Beauty Way, Los Angeles, CA 90210',
        phone: '(555) 123-4567',
        email: 'operations@sleekshop.com',
        website: 'https://sleekshop.com',
        taxId: '12-3456789',
        businessType: 'LLC',
        foundedYear: 2018,
        employeeCount: 45
      });

      // Mock Marketplace Configurations
      setMarketplaceConfigs([
        {
          id: '1',
          cartId: 24,
          name: 'Target',
          active: true,
          specialHandling: true,
          fulfillmentRules: ['Priority processing', 'Special packaging', 'Extended lead time'],
          leadTime: 3,
          packagingRequirements: ['Target-branded packaging', 'Barcode compliance', 'Special labeling'],
          hazmatCompliance: false,
          returnPolicy: '30-day return window',
          lastUpdated: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
        },
        {
          id: '2',
          cartId: 2,
          name: 'Amazon USA',
          active: true,
          specialHandling: true,
          fulfillmentRules: ['FBA preparation', 'Hazmat compliance', 'Amazon packaging standards'],
          leadTime: 2,
          packagingRequirements: ['Amazon FBA labels', 'Hazmat compliance', 'Standard packaging'],
          hazmatCompliance: true,
          returnPolicy: 'Amazon handles returns',
          lastUpdated: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
        },
        {
          id: '3',
          cartId: 11,
          name: 'Direct',
          active: true,
          specialHandling: false,
          fulfillmentRules: ['Fastest processing', 'Custom packaging available', 'Priority shipping'],
          leadTime: 1,
          packagingRequirements: ['SleekShop branding', 'Custom packaging options', 'Standard compliance'],
          hazmatCompliance: false,
          returnPolicy: '60-day return window',
          lastUpdated: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
        },
        {
          id: '4',
          cartId: 10,
          name: 'Walmart',
          active: true,
          specialHandling: true,
          fulfillmentRules: ['Bulk processing', 'Special labeling requirements', 'Extended lead time'],
          leadTime: 4,
          packagingRequirements: ['Walmart compliance', 'Bulk packaging', 'Special labeling'],
          hazmatCompliance: false,
          returnPolicy: 'Walmart handles returns',
          lastUpdated: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000)
        }
      ]);

      setIsLoading(false);
    };

    loadMockData();
  }, []);

  // Business Rules Management
  const handleAddRule = () => {
    setEditingRule(null);
    setRuleForm({
      name: '',
      description: '',
      category: 'operational',
      active: true,
      priority: 'medium',
      conditions: [''],
      actions: ['']
    });
    setRuleDialogOpen(true);
  };

  const handleEditRule = (rule: BusinessRule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      description: rule.description,
      category: rule.category,
      active: rule.active,
      priority: rule.priority,
      conditions: [...rule.conditions],
      actions: [...rule.actions]
    });
    setRuleDialogOpen(true);
  };

  const handleSaveRule = () => {
    if (editingRule) {
      // Update existing rule
      setBusinessRules(prev => prev.map(rule => 
        rule.id === editingRule.id 
          ? { ...rule, ...ruleForm, lastModified: new Date() }
          : rule
      ));
    } else {
      // Add new rule
      const newRule: BusinessRule = {
        id: Date.now().toString(),
        ...ruleForm,
        lastModified: new Date(),
        createdBy: 'admin@sleekshop.com',
        version: '1.0'
      };
      setBusinessRules(prev => [...prev, newRule]);
    }
    
    setRuleDialogOpen(false);
    showSnackbar('Business rule saved successfully', 'success');
  };

  const handleDeleteRule = (ruleId: string) => {
    setBusinessRules(prev => prev.filter(rule => rule.id !== ruleId));
    showSnackbar('Business rule deleted', 'success');
  };

  const handleToggleRule = (ruleId: string) => {
    setBusinessRules(prev => prev.map(rule => 
      rule.id === ruleId ? { ...rule, active: !rule.active } : rule
    ));
  };

  // Company Information Management
  const handleEditCompanyInfo = () => {
    setCompanyDialogOpen(true);
  };

  const handleSaveCompanyInfo = (updatedInfo: CompanyInfo) => {
    setCompanyInfo(updatedInfo);
    setCompanyDialogOpen(false);
    showSnackbar('Company information updated', 'success');
  };

  // Marketplace Configuration Management
  const handleAddMarketplace = () => {
    setEditingMarketplace(null);
    setMarketplaceForm({
      cartId: 0,
      name: '',
      active: true,
      specialHandling: false,
      fulfillmentRules: [''],
      leadTime: 2,
      packagingRequirements: [''],
      hazmatCompliance: false,
      returnPolicy: ''
    });
    setMarketplaceDialogOpen(true);
  };

  const handleEditMarketplace = (marketplace: MarketplaceConfig) => {
    setEditingMarketplace(marketplace);
    setMarketplaceForm({
      cartId: marketplace.cartId,
      name: marketplace.name,
      active: marketplace.active,
      specialHandling: marketplace.specialHandling,
      fulfillmentRules: [...marketplace.fulfillmentRules],
      leadTime: marketplace.leadTime,
      packagingRequirements: [...marketplace.packagingRequirements],
      hazmatCompliance: marketplace.hazmatCompliance,
      returnPolicy: marketplace.returnPolicy
    });
    setMarketplaceDialogOpen(true);
  };

  const handleSaveMarketplace = () => {
    if (editingMarketplace) {
      // Update existing marketplace
      setMarketplaceConfigs(prev => prev.map(mp => 
        mp.id === editingMarketplace.id 
          ? { ...mp, ...marketplaceForm, lastUpdated: new Date() }
          : mp
      ));
    } else {
      // Add new marketplace
      const newMarketplace: MarketplaceConfig = {
        id: Date.now().toString(),
        ...marketplaceForm,
        lastUpdated: new Date()
      };
      setMarketplaceConfigs(prev => [...prev, newMarketplace]);
    }
    
    setMarketplaceDialogOpen(false);
    showSnackbar('Marketplace configuration saved', 'success');
  };

  const handleDeleteMarketplace = (marketplaceId: string) => {
    setMarketplaceConfigs(prev => prev.filter(mp => mp.id !== marketplaceId));
    showSnackbar('Marketplace configuration deleted', 'success');
  };

  // Utility functions
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    return BUSINESS_RULE_CATEGORIES[category as keyof typeof BUSINESS_RULE_CATEGORIES]?.icon || <Settings />;
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading Business Rules Manager...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Psychology color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Admin Business Rules Manager
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage business logic, company information, and marketplace configurations
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Business Rules Section */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
                  <AutoAwesome sx={{ mr: 1, color: 'primary.main' }} />
                  Business Rules ({businessRules.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddRule}
                >
                  Add Rule
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {businessRules.map((rule) => (
                  <Paper key={rule.id} sx={{ p: 2, borderLeft: `4px solid ${rule.active ? '#4caf50' : '#9e9e9e'}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getCategoryIcon(rule.category)}
                        <Typography variant="h6">{rule.name}</Typography>
                        <Chip 
                          label={rule.priority} 
                          size="small" 
                          color={getPriorityColor(rule.priority) as any}
                        />
                        <Chip 
                          label={rule.category} 
                          size="small" 
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={rule.active}
                              onChange={() => handleToggleRule(rule.id)}
                              color="success"
                            />
                          }
                          label="Active"
                        />
                        <IconButton size="small" onClick={() => handleEditRule(rule)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteRule(rule.id)}>
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {rule.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Box>
                        <Typography variant="caption" fontWeight="bold">Conditions:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {rule.conditions.map((condition, index) => (
                            <Chip key={index} label={condition} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" fontWeight="bold">Actions:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {rule.actions.map((action, index) => (
                            <Chip key={index} label={action} size="small" variant="outlined" color="primary" />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Version {rule.version} • Modified {rule.lastModified.toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        By {rule.createdBy}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Company Information & Marketplace Configs */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={3}>
            {/* Company Information */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Business sx={{ mr: 1, color: 'primary.main' }} />
                      Company Information
                    </Typography>
                    <IconButton size="small" onClick={handleEditCompanyInfo}>
                      <Edit />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">{companyInfo?.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {companyInfo?.warehouseHours}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {companyInfo?.orderVolume}
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleEditCompanyInfo}
                  >
                    Edit Company Info
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Marketplace Configurations */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Store sx={{ mr: 1, color: 'primary.main' }} />
                      Marketplace Configs ({marketplaceConfigs.length})
                    </Typography>
                    <IconButton size="small" onClick={handleAddMarketplace}>
                      <Add />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {marketplaceConfigs.map((marketplace) => (
                      <Paper key={marketplace.id} sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {marketplace.name} (Cart ID: {marketplace.cartId})
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Lead time: {marketplace.leadTime} days
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip 
                              label={marketplace.active ? 'Active' : 'Inactive'} 
                              size="small" 
                              color={marketplace.active ? 'success' : 'default'}
                            />
                            {marketplace.specialHandling && (
                              <Chip 
                                label="Special" 
                                size="small" 
                                color="warning"
                              />
                            )}
                            <IconButton size="small" onClick={() => handleEditMarketplace(marketplace)}>
                              <Edit />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteMarketplace(marketplace.id)}>
                              <Delete />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Add />}
                    onClick={handleAddMarketplace}
                    sx={{ mt: 2 }}
                  >
                    Add Marketplace
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Business Rule Dialog */}
      <Dialog open={ruleDialogOpen} onClose={() => setRuleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRule ? 'Edit Business Rule' : 'Add Business Rule'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rule Name"
                value={ruleForm.name}
                onChange={(e) => setRuleForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={ruleForm.description}
                onChange={(e) => setRuleForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={ruleForm.category}
                  onChange={(e) => setRuleForm(prev => ({ ...prev, category: e.target.value as any }))}
                >
                  {Object.entries(BUSINESS_RULE_CATEGORIES).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={ruleForm.priority}
                  onChange={(e) => setRuleForm(prev => ({ ...prev, priority: e.target.value as any }))}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={ruleForm.active}
                    onChange={(e) => setRuleForm(prev => ({ ...prev, active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>Conditions</Typography>
              {ruleForm.conditions.map((condition, index) => (
                <TextField
                  key={index}
                  fullWidth
                  size="small"
                  label={`Condition ${index + 1}`}
                  value={condition}
                  onChange={(e) => {
                    const newConditions = [...ruleForm.conditions];
                    newConditions[index] = e.target.value;
                    setRuleForm(prev => ({ ...prev, conditions: newConditions }));
                  }}
                  sx={{ mb: 1 }}
                />
              ))}
              <Button
                size="small"
                onClick={() => setRuleForm(prev => ({ ...prev, conditions: [...prev.conditions, ''] }))}
              >
                Add Condition
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>Actions</Typography>
              {ruleForm.actions.map((action, index) => (
                <TextField
                  key={index}
                  fullWidth
                  size="small"
                  label={`Action ${index + 1}`}
                  value={action}
                  onChange={(e) => {
                    const newActions = [...ruleForm.actions];
                    newActions[index] = e.target.value;
                    setRuleForm(prev => ({ ...prev, actions: newActions }));
                  }}
                  sx={{ mb: 1 }}
                />
              ))}
              <Button
                size="small"
                onClick={() => setRuleForm(prev => ({ ...prev, actions: [...prev.actions, ''] }))}
              >
                Add Action
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRuleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveRule} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Company Information Dialog */}
      <Dialog open={companyDialogOpen} onClose={() => setCompanyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Company Information</DialogTitle>
        <DialogContent>
          {companyInfo && (
            <CompanyInfoForm
              companyInfo={companyInfo}
              onSave={handleSaveCompanyInfo}
              onCancel={() => setCompanyDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Marketplace Configuration Dialog */}
      <Dialog open={marketplaceDialogOpen} onClose={() => setMarketplaceDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMarketplace ? 'Edit Marketplace Configuration' : 'Add Marketplace Configuration'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Cart ID"
                type="number"
                value={marketplaceForm.cartId}
                onChange={(e) => setMarketplaceForm(prev => ({ ...prev, cartId: parseInt(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Marketplace Name"
                value={marketplaceForm.name}
                onChange={(e) => setMarketplaceForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Lead Time (days)"
                type="number"
                value={marketplaceForm.leadTime}
                onChange={(e) => setMarketplaceForm(prev => ({ ...prev, leadTime: parseInt(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Return Policy"
                value={marketplaceForm.returnPolicy}
                onChange={(e) => setMarketplaceForm(prev => ({ ...prev, returnPolicy: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={marketplaceForm.active}
                    onChange={(e) => setMarketplaceForm(prev => ({ ...prev, active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={marketplaceForm.specialHandling}
                    onChange={(e) => setMarketplaceForm(prev => ({ ...prev, specialHandling: e.target.checked }))}
                  />
                }
                label="Special Handling Required"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={marketplaceForm.hazmatCompliance}
                    onChange={(e) => setMarketplaceForm(prev => ({ ...prev, hazmatCompliance: e.target.checked }))}
                  />
                }
                label="Hazmat Compliance Required"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarketplaceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveMarketplace} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Company Information Form Component
interface CompanyInfoFormProps {
  companyInfo: CompanyInfo;
  onSave: (info: CompanyInfo) => void;
  onCancel: () => void;
}

function CompanyInfoForm({ companyInfo, onSave, onCancel }: CompanyInfoFormProps) {
  const [form, setForm] = useState(companyInfo);

  const handleSave = () => {
    onSave(form);
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Company Name"
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Warehouse Hours"
            value={form.warehouseHours}
            onChange={(e) => setForm(prev => ({ ...prev, warehouseHours: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Order Volume"
            value={form.orderVolume}
            onChange={(e) => setForm(prev => ({ ...prev, orderVolume: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Timezone"
            value={form.timezone}
            onChange={(e) => setForm(prev => ({ ...prev, timezone: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            value={form.address}
            onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            value={form.email}
            onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Website"
            value={form.website}
            onChange={(e) => setForm(prev => ({ ...prev, website: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Tax ID"
            value={form.taxId}
            onChange={(e) => setForm(prev => ({ ...prev, taxId: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Business Type"
            value={form.businessType}
            onChange={(e) => setForm(prev => ({ ...prev, businessType: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Founded Year"
            type="number"
            value={form.foundedYear}
            onChange={(e) => setForm(prev => ({ ...prev, foundedYear: parseInt(e.target.value) }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Employee Count"
            type="number"
            value={form.employeeCount}
            onChange={(e) => setForm(prev => ({ ...prev, employeeCount: parseInt(e.target.value) }))}
          />
        </Grid>
      </Grid>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save Changes</Button>
      </Box>
    </Box>
  );
}
